import { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import { PMDatePicker } from "../../../utils/PMDatePicker";
import { axiosWithToast, mapErrors } from "../../../Utils";
import PMSwitch from "../../../utils/PMSwitch";
import {
  cancelReportAgreement,
  fetchReport,
  newReportAgreement,
  resendReportAppointment,
  sendAgreementConfirmationReminder,
  sendUpdatedAppointmentToCustomer,
  updateReportAgreement,
} from "../../../../api/service/report";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { cancelReasons, confirmationTypes } from "./report-data";
import ModernEmployeeSelector from "../../../utils/Selector/ModernEmployeeSelector";
import { ReportAgreementVerifier2 } from "./utils";
import { PMNotice } from "../../../utils/notifications";
import SimplePrompt from "../../../utils/simplePrompt";
import { fetchEvents as fetchCalendarEvents } from "../../../../api/events";
import { fetchContact } from "../../../../api/customer/contact";
import ReportAgreementNewFlow from "./agreement-redesign/ReportAgreementNewFlow";
import { FormikSendRecipientPicker, PhoneRecipientShortcuts } from "../../shared/SendRecipientPicker";

function AgreementRecipientPicker({ report, projectContact, serviceAgreementContact, isLoadingContact, formik, onPick }) {
  const { t } = useTranslation();
  const options = [
    { key: "customer", label: t("Customer"), email: report?.customer?.email,
      mobile: report?.customer?.mobile, name: report?.customer?.name },
    { key: "contact", label: t("Contact"), email: report?.contact?.email,
      mobile: report?.contact?.mobile, name: report?.contact?.name || report?.contact?.displayTitle },
    { key: "project", label: isLoadingContact ? t("Loading...") : t("Project Contact"),
      email: projectContact?.email || report?.project?.contact?.email,
      mobile: projectContact?.mobile || report?.project?.contact?.mobile,
      name: projectContact?.name || projectContact?.displayTitle || report?.project?.contact?.name,
      disabled: isLoadingContact },
    { key: "agreement", label: t("Service Agreement Contact"),
      email: serviceAgreementContact?.email || report?.agreement?.contact?.email,
      mobile: serviceAgreementContact?.mobile || report?.agreement?.contact?.mobile,
      name: serviceAgreementContact?.name || serviceAgreementContact?.displayTitle || report?.agreement?.contact?.name },
  ].filter((option) => option.email || option.mobile);
  return <div className="space-y-3">
    {formik.values.sendSmsToCustomer && <PhoneRecipientShortcuts label={t("Phone number")} options={options} onSelect={(option) => onPick(option, formik)} />}
    {formik.values.sendEmailToCustomer && <FormikSendRecipientPicker
      formik={formik} emailField="to_email_address" nameField="recipient_name"
      options={options} customerId={report?.customer?.id}
    />}
  </div>;
}

function ReportEventsView({ events, planned, isLoading }) {
  const { t } = useTranslation();
  const items = Array.isArray(events) ? events : [];

  const formatDateTime = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    } catch (_e) {
      return String(value);
    }
  };

  return (
    <div className="border rounded-xl p-4 h-full max-h-[70vh] overflow-y-auto bg-white shadow-sm">
      <div className="mb-3">
        <h4 className="text-md font-semibold text-gray-800">{t("Events")}</h4>
        <p className="text-xs text-gray-500">{t("Your schedule for the selected day")}</p>
      </div>
      {isLoading ? (
        <div className="text-sm text-gray-500 italic">{t("Loading events...")}</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 italic">{t("No events to show")}</div>
      ) : (
        <ul className="space-y-3">
          {items.map((ev, index) => {
            const title = ev?.module_details?.displayTitle || ev?.title || `${t("Event")} #${index + 1}`;
            const description = ev?.module_details?.description || ev?.description || null;
            const type = ev?.module_details?.report_type || null;
            const when = ev?.start_time || ev?.created_at || null;
            const hasConflict = Boolean(ev?.__conflict === true);
            return (
              <li key={ev?.id || index} className={`bg-white border rounded-lg p-3 ${hasConflict ? "border-red-300" : "border-gray-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <span>{title}</span>
                      {hasConflict && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">{t("Conflict")}</span>
                      )}
                    </div>
                    {type ? (
                      <div className="text-xs">{type}</div>
                    ) : description ? (
                      <div className="text-xs">{description}</div>
                    ) : null}
                  </div>

                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
                  {ev?.start_time && <div><span className="font-medium">{t("Start")}:</span> {formatDateTime(ev.start_time)}</div>}
                  {ev?.end_time && <div><span className="font-medium">{t("End")}:</span> {formatDateTime(ev.end_time)}</div>}
                  {ev?.location && <div className="col-span-2"><span className="font-medium">{t("Location")}:</span> {ev.location}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {planned?.summary ? (
        <div className="mt-4 text-xs text-gray-600">
          <div className="font-medium">{t("Planned time")}</div>
          <div>{planned.summary}</div>
          {planned.notes && <div className="text-[11px] italic mt-1">{planned.notes}</div>}
        </div>
      ) : null}
    </div>
  );
}

function ReportEventsSidebar({ userId, date, fixedTime, time, timeFrom, timeTo }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ isLoading: false, events: [] });

  const toDate = (dStr, tStr) => {
    if (!dStr || !tStr) return null;
    try {
      return new Date(`${dStr}T${tStr}:00`);
    } catch (_e) {
      return null;
    }
  };

  const interval = (() => {
    if (!date) return null;
    if (fixedTime && time) {
      const start = toDate(date, time);
      const end = start ? new Date(start.getTime() + 60 * 60 * 1000) : null; // assume 60m window
      return { start, end, summary: `${date} ${time} - ${new Date(end).toTimeString().slice(0,5)}`, notes: t("Assumed 60 min duration for conflict check") };
    }
    if (!fixedTime && timeFrom && timeTo) {
      const start = toDate(date, timeFrom);
      const end = toDate(date, timeTo);
      return { start, end, summary: `${date} ${timeFrom} - ${timeTo}` };
    }
    return null;
  })();

  const overlaps = (aStart, aEnd, bStart, bEnd) => {
    if (!aStart || !aEnd || !bStart || !bEnd) return false;
    return aStart < bEnd && bStart < aEnd;
  };

  useEffect(() => {
    let abort = false;
    if (!userId || !date) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }
    setState({ isLoading: true, events: [] });
    const params = {
      date: date,
      view: "day",
      users: [String(userId)].join(","),
    };
    fetchCalendarEvents(params)
      .then((res) => {
        if (abort) return;
        const items = Array.isArray(res.data) ? res.data : [];
        // Compute conflicts against planned interval
        const withConflicts = items.map((ev) => {
          let evStart = ev?.start_time ? new Date(ev.start_time) : null;
          let evEnd = ev?.end_time ? new Date(ev.end_time) : null;
          // If missing times but working_hours has schedule for the date
          if ((!evStart || !evEnd) && ev?.working_hours && Array.isArray(ev.working_hours.schedule)) {
            const schedule = ev.working_hours.schedule.find((s) => s.date === date && !s.day_off);
            if (schedule?.start_time && schedule?.end_time) {
              evStart = toDate(date, schedule.start_time);
              evEnd = toDate(date, schedule.end_time);
            }
          }
          const conflict = interval ? overlaps(interval.start, interval.end, evStart, evEnd) : false;
          return { ...ev, __conflict: conflict };
        });
        setState({ isLoading: false, events: withConflicts });
      })
      .catch((_e) => {
        if (abort) return;
        setState({ isLoading: false, events: [] });
      });
    return () => {
      abort = true;
    };
  }, [userId, date, fixedTime, time, timeFrom, timeTo]);

  const isOpen = !state.isLoading && Array.isArray(state.events) && state.events.length > 0;

  if (!isOpen) {
    return <div className="hidden" aria-hidden="true" />;
  }

  return (
    <div
      className="w-full max-w-full overflow-hidden lg:w-[22rem] lg:max-w-[22rem]"
      aria-hidden={!isOpen}
    >
      <ReportEventsView events={state.events} isLoading={state.isLoading} planned={interval} />
    </div>
  );
}

export default function ReportAgreementModal({ onRefresh = null, ...props }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isCompanyAdmin = Boolean(
    currentUser?.roles?.some((r) => r.name === "admin")
  );

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    report: {},
  });

  const { open, onClose, selected } = props;

  const { report, isLoading, error, errors } = itemState;

  const getReport = () => {
    const params = {
      fields:
        "id,customer.id,customer.mobile,customer.email,customer.name,contact.id,contact.email,contact.mobile,contact.name,project.id,project.contact.id,project.contact.email,project.contact.mobile,project.contact.name,agreement.id,agreement.contact.id,agreement.contact.email,agreement.contact.mobile,agreement.contact.name,appointment,executing_employee,operations",
    };
    return fetchReport(selected.id, params)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            report: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const postReport = (action, payload) => {
    return reportPostAction(action, payload)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            report: res.data,
          }));
        }
        onRefresh(res.data);
        return true;
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          report: {},
          error: true,
          errors: errors,
        }));
      });
  };

  const reportPostAction = (action, payload) => {
    if (action === "new") {
      return newReportAgreement(payload);
    }
    if (action === "update") {
      return updateReportAgreement(payload);
    }
    if (action === "cancel") {
      return cancelReportAgreement(payload);
    }
  };

  useEffect(() => {
    if (selected?.id) {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: true,
        report: {},
        error: false,
        errors: {},
      }));
      if (open === true) {
        getReport();
      }
    }
  }, [dispatch, open]);

  const [executingEmployeeSelectorOpen, setExecutingEmployeeSelectorOpen] =
    useState(false);
  const [executingEmployeeDetail, setExecutingEmployeeDetail] = useState(null);

  useEffect(() => {
    if (!open) {
      setExecutingEmployeeSelectorOpen(false);
      return;
    }
    const ex = report?.executing_employee;
    if (ex?.id) {
      setExecutingEmployeeDetail({
        id: ex.id,
        label: ex.displayTitle || ex.name,
      });
    } else if (!report?.appointment?.has_agreement) {
      setExecutingEmployeeDetail(null);
    }
  }, [
    open,
    report?.id,
    report?.executing_employee?.id,
    report?.executing_employee?.name,
    report?.appointment?.has_agreement,
  ]);

  // Load project contact and service agreement contact when report is loaded
  useEffect(() => {
    const loadContacts = async () => {
      if (open && report?.id) {
        // Load project contact if available
        if (report?.project?.contact?.id) {
          setIsLoadingContact(true);
          try {
            const response = await fetchContact(report.project.contact.id);
            if (response?.data) {
              setProjectContact(response.data);
            }
          } catch (error) {
            console.error("Error fetching project contact:", error);
          } finally {
            setIsLoadingContact(false);
          }
        } else {
          setProjectContact(null);
        }

        // Load service agreement contact if available
        if (report?.agreement?.contact?.id) {
          try {
            const response = await fetchContact(report.agreement.contact.id);
            if (response?.data) {
              setServiceAgreementContact(response.data);
            }
          } catch (error) {
            console.error("Error fetching service agreement contact:", error);
          }
        } else {
          setServiceAgreementContact(null);
        }
      } else {
        setProjectContact(null);
        setServiceAgreementContact(null);
      }
    };

    loadContacts();
  }, [open, report?.id, report?.project?.contact?.id, report?.agreement?.contact?.id]);

  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingReminderPrompt, setSendingReminderPrompt] = useState(false);
  const [sendingUpdatedPrompt, setSendingUpdatedPrompt] = useState(false);
  const [sendingUpdated, setSendingUpdated] = useState(false);
  const [resendingAppointment, setResendingAppointment] = useState(false);
  const [resendAppointmentPrompt, setResendAppointmentPrompt] = useState(false);
  const [pendingManualConfirmationType, setPendingManualConfirmationType] =
    useState(null);
  const [manualConfirmPromptOpen, setManualConfirmPromptOpen] = useState(false);
  const [pendingCancelReason, setPendingCancelReason] = useState(null);
  const [cancelAgreementPromptOpen, setCancelAgreementPromptOpen] =
    useState(false);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [projectContact, setProjectContact] = useState(null);
  const [serviceAgreementContact, setServiceAgreementContact] = useState(null);

  async function agreementConfirmationReminder(report) {
    // send reminder

    setSendingReminder(true);

    const params = {
      id: report.id,
      bySms: true,
    };

    await sendAgreementConfirmationReminder(params)
      .then((res) => {
        setSendingReminder(false);
        if (res.status === 204) {
          toast.success("Reminder sent successfully");
        } else {
          toast.error("Reminder could not be sent");
        }
      })
      .catch((err) => {
        setSendingReminder(false);
        toast.error("Reminder could not be sent");
      });
  }

  async function resendAppointmentToCustomer(report) {
    // send reminder

    setResendingAppointment(true);

    const params = {
      id: report.id,
    };

    await resendReportAppointment(params)
      .then((res) => {
        setResendingAppointment(false);
        if (res.status === 200) {
          toast.success("Resent appointment successfully");
        } else {
          toast.error("Text Message could not be resent");
        }
      })
      .catch((err) => {
        setResendingAppointment(false);
        toast.error("Text Message could not be resent");
      });
  }

  async function sendUpdatedAppointment(report) {
    // send reminder

    setSendingUpdated(true);

    const params = {
      id: report.id,
    };

    await sendUpdatedAppointmentToCustomer(params)
      .then((res) => {
        setResendingAppointment(false);
        if (res.status === 200) {
          toast.success("Resent appointment successfully");
        } else {
          toast.error("Text Message could not be resent");
        }
      })
      .catch((err) => {
        setResendingAppointment(false);
        toast.error("Text Message could not be resent");
      });
  }

  const handleReceiverChange = (receiver, formik) => {
    if (receiver?.email) {
      formik.setFieldValue("to_email_address", receiver.email);
    }
    if (receiver?.mobile) {
      formik.setFieldValue("to_phone_number", receiver.mobile);
    }
    // Update send switches based on available contact methods
    if (receiver?.email) {
      formik.setFieldValue("sendEmailToCustomer", true);
    }
    if (receiver?.mobile) {
      formik.setFieldValue("sendSmsToCustomer", true);
    }
  };

  if (
    open &&
    !isLoading &&
    !error &&
    report?.id &&
    !report?.appointment?.has_agreement
  ) {
    return (
      <ReportAgreementNewFlow
        open={open}
        onClose={onClose}
        report={report}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full justify-center text-center sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="flex h-[100dvh] w-full max-w-none transform flex-col overflow-hidden rounded-none bg-white text-left align-middle shadow-xl ring-1 ring-black/5 transition-all sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl">
                <div className="flex flex-none items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6 sm:pt-4">
                  <div className="min-w-0">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold tracking-tight text-gray-900"
                    >
                      {report?.appointment?.has_agreement
                        ? t("report.agreement.update")
                        : t("report.agreement.new")}
                    </Dialog.Title>
                    {!report?.appointment?.has_agreement ? (
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
                        {t("report.agreement.modal_intro")}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t("Close")}
                    className="-mr-1.5 -mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">

                  {isLoading ? (
                    <ResourceLoading />
                  ) : error ? (
                    <ResourceError errors={errors} />
                  ) : !report.id ? (
                    <ResourceEmpty />
                  ) : (
                    <ReportAgreementVerifier2 report={report}>
                      <Formik
                        initialValues={{
                          id: report?.id,
                          has_agreement: report?.appointment?.has_agreement,
                          executing_employee: report?.appointment?.has_agreement
                            ? report?.executing_employee?.id
                            : isCompanyAdmin
                              ? report?.executing_employee?.id
                              : currentUser?.id ?? report?.executing_employee?.id,
                          agreement_time_too: report?.appointment?.time_too,
                          agreement_time_from: report?.appointment?.time_from,
                          agreement_time: report?.appointment?.time,
                          agreement_date: report?.appointment?.date,
                          to_phone_number: report?.customer?.mobile || null,
                          to_email_address: report?.customer?.email || null,
                          fixedTime: report?.appointment?.time ? true : false,
                          manualConfirmationType: null,
                          cancelReason: null,
                          sendAgreementToCustomer: true,
                          sendSmsToCustomer: report?.customer?.mobile
                            ? true
                            : false,
                          sendEmailToCustomer: report?.customer?.email
                            ? true
                            : false,
                          appointmentConfirmationRequired: true,
                          submit: null,
                        }}
                        enableReinitialize={true}
                        validationSchema={Yup.object({
                          has_agreement: Yup.boolean().required(),
                          fixedTime: Yup.boolean().required(),
                          sendAgreementToCustomer: Yup.boolean(),
                          sendSmsToCustomer: Yup.boolean().required(),
                          sendEmailToCustomer: Yup.boolean().required(),

                          agreement_time_from: Yup.string()
                            .nullable()
                            .when(["has_agreement", "fixedTime"], {
                              is: (has_agreement, fixedTime) =>
                                !has_agreement && !fixedTime,
                              then: Yup.string()
                                .nullable()
                                .required("Tidsrom fra er påkrevd"),
                              otherwise: Yup.string().nullable().notRequired(),
                            }),

                          agreement_time_too: Yup.string()
                            .nullable()
                            .when(["has_agreement", "fixedTime"], {
                              is: (has_agreement, fixedTime) =>
                                !has_agreement && !fixedTime,
                              then: Yup.string()
                                .nullable()
                                .required("Tidsrom til er påkrevd"),
                              otherwise: Yup.string().nullable().notRequired(),
                            }),

                          agreement_time: Yup.string()
                            .nullable()
                            .when(["has_agreement", "fixedTime"], {
                              is: (has_agreement, fixedTime) =>
                                !has_agreement && fixedTime,
                              then: Yup.string()
                                .nullable()
                                .required("Tidspunkt er påkrevd"),
                              otherwise: Yup.string().nullable().notRequired(),
                            }),

                          agreement_date: Yup.string()
                            .nullable()
                            .when("has_agreement", {
                              is: false,
                              then: (schema) =>
                                schema.required(
                                  t("report.agreement.validation.date_required")
                                ),
                              otherwise: (schema) =>
                                schema.nullable().notRequired(),
                            }),

                          executing_employee: Yup.mixed().when(
                            "has_agreement",
                            {
                              is: false,
                              then: (schema) =>
                                schema.required(
                                  t(
                                    "report.agreement.validation.executing_required"
                                  )
                                ),
                              otherwise: (schema) =>
                                schema.nullable().notRequired(),
                            }
                          ),

                          to_phone_number: Yup.string().when(
                            ["sendSmsToCustomer", "sendAgreementToCustomer"],
                            {
                              is: (sms, sendAgreement) =>
                                sendAgreement !== false && sms === true,
                              then: (schema) =>
                                schema.required(
                                  t(
                                    "report.agreement.validation.phone_for_sms"
                                  )
                                ),
                              otherwise: (schema) =>
                                schema.nullable().notRequired(),
                            }
                          ),

                          to_email_address: Yup.string().when(
                            ["sendEmailToCustomer", "sendAgreementToCustomer"],
                            {
                              is: (email, sendAgreement) =>
                                sendAgreement !== false && email === true,
                              then: (schema) =>
                                schema
                                  .required(
                                    t(
                                      "report.agreement.validation.email_for_email"
                                    )
                                  )
                                  .email(
                                    t(
                                      "report.agreement.validation.email_for_email"
                                    )
                                  ),
                              otherwise: (schema) =>
                                schema.nullable().notRequired(),
                            }
                          ),

                          customer: Yup.string(),
                        })}
                        onSubmit={(values, helpers) => {
                          try {
                            const payload = { ...values };
                            const sendAgreement =
                              payload.sendAgreementToCustomer !== false;
                            delete payload.sendAgreementToCustomer;
                            if (!sendAgreement) {
                              payload.sendSmsToCustomer = false;
                              payload.sendEmailToCustomer = false;
                            }

                            axiosWithToast(
                              reportPostAction(
                                report?.appointment?.has_agreement
                                  ? values.cancelReason !== null
                                    ? "cancel"
                                    : "update"
                                  : "new",
                                payload
                              ),
                              (res) => {
                                onRefresh(res);
                              }, // On Refresh
                              () => {}, // On close
                              (data) => {
                                console.log(data);
                                setItemState((prevState) => ({
                                  ...prevState,
                                  isLoading: false,
                                  report: data,
                                }));
                              } // Set data
                            );

                            helpers.setStatus({ success: true });
                            helpers.setSubmitting(false);
                          } catch (err) {
                            toast.error("Something went wrong...");
                            console.error(err);
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => {
                          const execId = formik.values.executing_employee;
                          const executingButtonLabel =
                            execId != null && execId !== ""
                              ? executingEmployeeDetail &&
                                String(executingEmployeeDetail.id) ===
                                  String(execId)
                                ? executingEmployeeDetail.label
                                : t("report.agreement.change_executing")
                              : t("report.agreement.select_executing");

                          return (
                    <>
                    <div className="mt-2 grid grid-cols-1 gap-5 lg:flex lg:items-start lg:gap-6">
                      <div className="min-w-0 lg:flex-1">
                                <Form>
                              {report?.case?.id && (
                                <PMNotice
                                  title={
                                    "Rapport er knyttet til SAK-" +
                                    report?.case?.case_number
                                  }
                                  description={
                                    "Oppdatering her vil føres inn i saken automatisk"
                                  }
                                  type={"info"}
                                />
                              )}

                              <div className="mt-2 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-x-8 md:gap-y-6">
                                {report?.appointment?.has_agreement ? (
                                  <>
                                    <div className="col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100/80">
                                      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          {t("report.agreement.section.followup")}
                                        </h4>
                                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                                          {t(
                                            "report.agreement.section.followup_help"
                                          )}
                                        </p>
                                      </div>
                                      <div className="p-5 sm:p-6">
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                          {!report?.appointment?.approved ? (
                                            <>
                                              <button
                                                type="button"
                                                disabled={sendingReminder}
                                                onClick={() =>
                                                  setSendingReminderPrompt(true)
                                                }
                                                className={`w-full rounded-lg bg-blue-600 px-4 py-3.5 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 ${
                                                  report?.appointment
                                                    ?.agreement_method ===
                                                  "TextMessage"
                                                    ? ""
                                                    : "sm:col-span-2"
                                                }`}
                                              >
                                                {t("Send confirmation reminder")}
                                              </button>
                                              {report?.appointment
                                                ?.agreement_method ===
                                              "TextMessage" ? (
                                                <button
                                                  type="button"
                                                  disabled={resendingAppointment}
                                                  onClick={() =>
                                                    setResendAppointmentPrompt(
                                                      true
                                                    )
                                                  }
                                                  className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-3.5 text-center text-sm font-semibold text-blue-900 shadow-sm transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                  {t("Send avtale på nytt")}
                                                </button>
                                              ) : null}
                                            </>
                                          ) : (
                                            <button
                                              type="button"
                                              disabled={sendingReminder}
                                              onClick={() =>
                                                setSendingUpdatedPrompt(true)
                                              }
                                              className="w-full rounded-lg bg-blue-600 px-4 py-3.5 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-2"
                                            >
                                              {t("Send oppdatert avtale")}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    <SimplePrompt
                                      title={t(
                                        "report.agreement.prompt.reminder_title"
                                      )}
                                      description={t(
                                        "report.agreement.prompt.reminder_body"
                                      )}
                                      onAccept={() =>
                                        agreementConfirmationReminder(report)
                                      }
                                      open={sendingReminderPrompt}
                                      onClose={() =>
                                        setSendingReminderPrompt(false)
                                      }
                                    />
                                    <SimplePrompt
                                      title={t(
                                        "report.agreement.prompt.resend_title"
                                      )}
                                      description={t(
                                        "report.agreement.prompt.resend_body"
                                      )}
                                      onAccept={() =>
                                        resendAppointmentToCustomer(report)
                                      }
                                      open={resendAppointmentPrompt}
                                      onClose={() =>
                                        setResendAppointmentPrompt(false)
                                      }
                                    />
                                    <SimplePrompt
                                      title={t(
                                        "report.agreement.prompt.updated_appointment_title"
                                      )}
                                      description={t(
                                        "report.agreement.prompt.updated_appointment_body"
                                      )}
                                      onAccept={() =>
                                        sendUpdatedAppointment(report)
                                      }
                                      open={sendingUpdatedPrompt}
                                      onClose={() =>
                                        setSendingUpdatedPrompt(false)
                                      }
                                    />

                                    {!report?.appointment?.approved ? (
                                      <div className="col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100/80">
                                        <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
                                          <h4 className="text-sm font-semibold text-gray-900">
                                            {t(
                                              "report.agreement.section.manual_confirm"
                                            )}
                                          </h4>
                                          <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                                            {t(
                                              "report.agreement.section.manual_confirm_help"
                                            )}
                                          </p>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-3 sm:p-6">
                                          {confirmationTypes.map((opt) => (
                                            <button
                                              key={opt.value}
                                              type="button"
                                              onClick={() => {
                                                setPendingManualConfirmationType(
                                                  opt.value
                                                );
                                                setManualConfirmPromptOpen(true);
                                              }}
                                              className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-center text-sm font-semibold text-emerald-900 shadow-sm transition hover:bg-emerald-100"
                                            >
                                              {t(opt.label)}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null}

                                    <div className="col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100/80">
                                      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          {t(
                                            "report.agreement.section.cancel_agreement"
                                          )}
                                        </h4>
                                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                                          {t(
                                            "report.agreement.section.cancel_agreement_help"
                                          )}
                                        </p>
                                      </div>
                                      <div className="space-y-5 p-5 sm:p-6">
                                        <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4">
                                          <PMSwitch
                                            name="sendSmsToCustomer"
                                            label={t("send-sms.customer")}
                                            {...formik}
                                          />
                                          <p className="mt-2 text-xs leading-relaxed text-gray-600">
                                            {t(
                                              "report.agreement.cancel_sms_hint"
                                            )}
                                          </p>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                          {cancelReasons.map((opt) => (
                                              <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => {
                                                  setPendingCancelReason(
                                                    opt.value
                                                  );
                                                  setCancelAgreementPromptOpen(
                                                    true
                                                  );
                                                }}
                                                className="w-full rounded-lg border border-red-200 bg-red-50/80 px-4 py-3.5 text-left text-sm font-semibold text-red-900 shadow-sm transition hover:bg-red-100"
                                              >
                                                {t(opt.label)}
                                              </button>
                                            ))}
                                        </div>
                                      </div>
                                    </div>

                                    <SimplePrompt
                                      title={t(
                                        "report.agreement.prompt.confirm_manual_title"
                                      )}
                                      description={t(
                                        "report.agreement.prompt.confirm_manual_body",
                                        {
                                          method:
                                            pendingManualConfirmationType !==
                                            null
                                              ? t(
                                                  confirmationTypes.find(
                                                    (c) =>
                                                      c.value ===
                                                      pendingManualConfirmationType
                                                  )?.label || ""
                                                )
                                              : "",
                                        }
                                      )}
                                      onAccept={() => {
                                        if (pendingManualConfirmationType === null)
                                          return;
                                        void formik
                                          .setValues({
                                            ...formik.values,
                                            manualConfirmationType:
                                              pendingManualConfirmationType,
                                            cancelReason: null,
                                          })
                                          .then(() => formik.submitForm());
                                      }}
                                      open={manualConfirmPromptOpen}
                                      onClose={() => {
                                        setManualConfirmPromptOpen(false);
                                        setPendingManualConfirmationType(null);
                                      }}
                                    />
                                    <SimplePrompt
                                      title={t(
                                        "report.agreement.prompt.cancel_agreement_title"
                                      )}
                                      description={t(
                                        "report.agreement.prompt.cancel_agreement_body",
                                        {
                                          reason:
                                            pendingCancelReason !== null
                                              ? t(
                                                  cancelReasons.find(
                                                    (r) =>
                                                      r.value ===
                                                      pendingCancelReason
                                                  )?.label || ""
                                                )
                                              : "",
                                        }
                                      )}
                                      onAccept={() => {
                                        if (pendingCancelReason === null) return;
                                        void formik
                                          .setValues({
                                            ...formik.values,
                                            cancelReason: pendingCancelReason,
                                            manualConfirmationType: null,
                                          })
                                          .then(() => formik.submitForm());
                                      }}
                                      open={cancelAgreementPromptOpen}
                                      onClose={() => {
                                        setCancelAgreementPromptOpen(false);
                                        setPendingCancelReason(null);
                                      }}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <div className="col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100/80">
                                      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          {t("report.agreement.section.schedule")}
                                        </h4>
                                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                                          {t(
                                            "report.agreement.section.schedule_help"
                                          )}
                                        </p>
                                      </div>
                                      <div className="space-y-5 p-5 sm:p-6">
                                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-5">
                                          {isCompanyAdmin ? (
                                            <div className="min-w-0">
                                              <span className="mb-1.5 block text-sm font-medium text-gray-700">
                                                {t("Executing Employee")}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setExecutingEmployeeSelectorOpen(
                                                    true
                                                  )
                                                }
                                                className="flex w-full min-w-0 items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left text-sm text-gray-900 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-50"
                                              >
                                                <span className="truncate pr-2">
                                                  {executingButtonLabel}
                                                </span>
                                              </button>
                                              {formik.touched
                                                .executing_employee &&
                                              formik.errors
                                                .executing_employee ? (
                                                <p className="mt-2 text-sm text-red-600">
                                                  {
                                                    formik.errors
                                                      .executing_employee
                                                  }
                                                </p>
                                              ) : null}
                                            </div>
                                          ) : (
                                            <div className="min-w-0">
                                              <span className="mb-1.5 block text-sm font-medium text-gray-700">
                                                {t("Executing Employee")}
                                              </span>
                                              <p className="rounded-lg border border-transparent bg-gray-50 px-3 py-2.5 text-sm leading-relaxed text-gray-900">
                                                {report?.executing_employee &&
                                                String(
                                                  report.executing_employee.id
                                                ) ===
                                                  String(
                                                    formik.values
                                                      .executing_employee
                                                  )
                                                  ? report.executing_employee
                                                      .name
                                                  : String(currentUser?.id) ===
                                                      String(
                                                        formik.values
                                                          .executing_employee
                                                      )
                                                    ? currentUser?.name ||
                                                      currentUser?.email ||
                                                      t(
                                                        "report.agreement.executing_you"
                                                      )
                                                    : t("not_chosen")}
                                              </p>
                                            </div>
                                          )}

                                          <div className="min-w-0">
                                            <PMDatePicker
                                              name="agreement_date"
                                              label={t("Agreement date")}
                                              value={
                                                formik.values.agreement_date ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                formik.setFieldValue(
                                                  "agreement_date",
                                                  e.target.value
                                                );
                                              }}
                                              errors={
                                                formik.errors.agreement_date
                                              }
                                              touched={
                                                formik.touched.agreement_date
                                              }
                                              validationRules={{
                                                userId: formik.values
                                                  .executing_employee
                                                  ? Number(
                                                      formik.values
                                                        .executing_employee
                                                    )
                                                  : undefined,
                                              }}
                                            />
                                          </div>

                                          <div className="border-t border-gray-100 pt-5 md:col-span-2">
                                            <PMSwitch
                                              name="fixedTime"
                                              type="time"
                                              label={t("Fixed time")}
                                              {...formik}
                                            />
                                          </div>

                                          {formik.values.fixedTime ? (
                                            <>
                                              <p className="text-sm leading-relaxed text-gray-600 md:col-span-2">
                                                Nå velger du spesefikk tid for
                                                avtalen. Det betyr at oppmøte
                                                skjer ved valgt tid
                                              </p>
                                              <div className="max-w-xs md:col-span-2">
                                                <PMField
                                                  name="agreement_time"
                                                  type="time"
                                                  label={t("Fixed time")}
                                                  {...formik}
                                                />
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              <p className="text-sm leading-relaxed text-gray-600 md:col-span-2">
                                                Nå velger du tidsrom for
                                                avtalen. Det betyr at oppmøte
                                                skjer innenfor valgt tidsrom
                                              </p>
                                              <div className="min-w-0">
                                                <PMField
                                                  name="agreement_time_from"
                                                  type="time"
                                                  label={t("Timeframe from")}
                                                  {...formik}
                                                />
                                              </div>
                                              <div className="min-w-0">
                                                <PMField
                                                  name="agreement_time_too"
                                                  type="time"
                                                  label={t("Timeframe to")}
                                                  {...formik}
                                                />
                                              </div>
                                            </>
                                          )}
                                        </div>

                                        <div className="border-t border-gray-100 pt-5">
                                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start md:gap-6">
                                            <PMSwitch
                                              name="appointmentConfirmationRequired"
                                              type="time"
                                              label={t(
                                                "Appointment confirmation required"
                                              )}
                                              {...formik}
                                            />
                                            <p className="text-sm leading-relaxed text-gray-600 md:pt-1">
                                              {formik.values
                                                .appointmentConfirmationRequired
                                                ? t(
                                                    "report.agreement.confirm_hint_required"
                                                  )
                                                : t(
                                                    "report.agreement.confirm_hint_optional"
                                                  )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-span-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100/80">
                                      <div className="border-b border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          {t("report.agreement.section.notify")}
                                        </h4>
                                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                                          {t(
                                            "report.agreement.section.notify_help"
                                          )}
                                        </p>
                                      </div>
                                      <div className="space-y-6 p-5 sm:p-6">
                                        <div className="rounded-lg border border-gray-200 bg-slate-50/90 p-4 sm:p-5">
                                          <PMSwitch
                                            name="sendAgreementToCustomer"
                                            label={t(
                                              "report.agreement.send_to_customer"
                                            )}
                                            {...formik}
                                          />
                                          <p className="mt-3 text-sm leading-relaxed text-gray-600">
                                            {formik.values
                                              .sendAgreementToCustomer !== false
                                              ? t(
                                                  "report.agreement.send_to_customer_hint_on"
                                                )
                                              : t(
                                                  "report.agreement.send_to_customer_hint_off"
                                                )}
                                          </p>
                                        </div>

                                        {formik.values.sendAgreementToCustomer !==
                                        false ? (
                                          <>
                                            <div className="flex flex-wrap gap-x-8 gap-y-4">
                                              <PMSwitch
                                                name="sendSmsToCustomer"
                                                type="time"
                                                label={t("send-sms.customer")}
                                                {...formik}
                                              />
                                              <PMSwitch
                                                name="sendEmailToCustomer"
                                                type="time"
                                                label={t(
                                                  "Send email to customer"
                                                )}
                                                {...formik}
                                              />
                                            </div>

                                            <AgreementRecipientPicker
                                              report={report}
                                              projectContact={projectContact}
                                              serviceAgreementContact={serviceAgreementContact}
                                              isLoadingContact={isLoadingContact}
                                              formik={formik}
                                              onPick={handleReceiverChange}
                                            />

                                        {(formik.values.sendSmsToCustomer ||
                                          formik.values
                                            .sendEmailToCustomer) && (
                                          <div className="col-span-full grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                                            {formik.values.sendSmsToCustomer ? (
                                              <div className="min-w-0">
                                                <PMField
                                                  name="to_phone_number"
                                                  type="text"
                                                  label={t("Phone number")}
                                                  {...formik}
                                                />
                                              </div>
                                            ) : null}

                                          </div>
                                        )}
                                      </>
                                    ) : null}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>

                              <div
                                className={`sticky bottom-0 -mx-5 -mb-4 mt-6 flex gap-3 border-t border-gray-100 bg-white px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:static sm:mx-0 sm:mb-0 sm:px-0 sm:pb-0 ${
                                  report?.appointment?.has_agreement
                                    ? "justify-end"
                                    : "justify-between"
                                }`}
                              >
                                <button
                                  type="button"
                                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                  onClick={onClose}
                                >
                                  {t("Close")}
                                </button>

                                {!report?.appointment?.has_agreement ? (
                                  <button
                                    type="button"
                                    className="inline-flex min-h-11 items-center justify-center rounded-lg border border-transparent bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                                    onClick={formik.submitForm}
                                  >
                                    {report?.id ? t("Update") : t("Create")}
                                  </button>
                                ) : null}
                              </div>
                                </Form>
                              </div>
                              <div className="lg:flex-none">
                                <ReportEventsSidebar
                                  userId={formik.values.executing_employee || undefined}
                                  date={formik.values.agreement_date || undefined}
                                  fixedTime={Boolean(formik.values.fixedTime)}
                                  time={formik.values.agreement_time || undefined}
                                  timeFrom={formik.values.agreement_time_from || undefined}
                                  timeTo={formik.values.agreement_time_too || undefined}
                                />
                              </div>
                            </div>
                    {isCompanyAdmin && executingEmployeeSelectorOpen
                      ? createPortal(
                          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                            <div
                              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                              onClick={() =>
                                setExecutingEmployeeSelectorOpen(false)
                              }
                              aria-hidden="true"
                            />
                            <div className="relative z-[101] w-full max-w-md max-h-[90vh] overflow-hidden rounded-lg shadow-xl bg-white">
                              <ModernEmployeeSelector
                                includeHired
                                open={executingEmployeeSelectorOpen}
                                selectedEmployees={
                                  formik.values.executing_employee
                                    ? [
                                        executingEmployeeDetail &&
                                        String(executingEmployeeDetail.id) ===
                                          String(
                                            formik.values.executing_employee
                                          )
                                          ? {
                                              id: formik.values
                                                .executing_employee,
                                              name: executingEmployeeDetail.label,
                                              displayTitle:
                                                executingEmployeeDetail.label,
                                            }
                                          : {
                                              id: formik.values
                                                .executing_employee,
                                            },
                                      ]
                                    : []
                                }
                                onChange={(selected) => {
                                  if (selected.length > 0) {
                                    const emp = selected[0];
                                    formik.setFieldValue(
                                      "executing_employee",
                                      emp.id
                                    );
                                    formik.setFieldTouched(
                                      "executing_employee",
                                      true
                                    );
                                    setExecutingEmployeeDetail({
                                      id: emp.id,
                                      label:
                                        emp.displayTitle || emp.name,
                                    });
                                  }
                                  setExecutingEmployeeSelectorOpen(false);
                                }}
                                onClose={() =>
                                  setExecutingEmployeeSelectorOpen(false)
                                }
                                multiple={false}
                                filters={[
                                  { name: "role", value: "service" },
                                ]}
                              />
                            </div>
                          </div>,
                          document.body
                        )
                      : null}
                    </>
                          );
                        }}
                      </Formik>
                    </ReportAgreementVerifier2>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
